import React, { useEffect, useState } from "react";
import { Serializer } from "survey-core";
import { SurveyCreator, SurveyCreatorComponent } from "survey-creator-react";
import { Action } from "survey-core";
import "../css/survey-creator-core.scss";
import "survey-core/survey-core.css";
import "survey-creator-core/survey-creator-core.css";
import { Col, Container, Row, Spinner } from "reactstrap";
import { customThemeVariables } from "../helpers/theme";
import { getLocaleStrings, registerCreatorTheme } from "survey-creator-core";
import { useNavigate } from "react-router-dom";
import { LIBRARY_PATHS, VISIT_PATHS } from "../../../../Routes/paths";
import { runCompareValidation } from "modules/Library/CRF/components/Survey/Validation/compareValidation";
import { runWindowValidation } from "modules/Library/CRF/components/Survey/Validation/windowValidation";
import {
  applyDependencyVisibility,
  runDependencyRequireValidation,
  handleDependencyValueChange,
} from "modules/Library/CRF/components/Survey/Validation/dependencyUtils";
import {
  runConditionalRangeValidation,
  runMinMaxValidation,
  runValueValidation,
} from "modules/Library/CRF/components/Survey/Validation/ConditionalRangeValidation";
import { registerSurveyProperties } from "modules/Library/CRF/components/Survey/Serializer/SurveySerializersConfig";
import {
  registerPropertyEditorSync,
  registerTemplateGenerators,
} from "modules/Library/CRF/components/Survey/Events/surveyEvents";
import { applySurveyPropertyVisibility } from "modules/Library/CRF/components/Survey/Creator/surveyPropertyVisibility";
import { setupLocale } from "survey-core";
import FullPageLoader from "Components/Common/FullPageLoader";

// ---------------------------------------------------------
// Initialize Serializer settings once
// ---------------------------------------------------------

registerSurveyProperties();

/* =========================================================
   Types
========================================================= */

interface SurveyBuilderProps {
  initialJSON?: any;
  onSave: (payload: any) => void;
  isLoading?: boolean;
}

/* =========================================================
   Component
========================================================= */

const SurveyBuilder: React.FC<SurveyBuilderProps> = ({
  initialJSON = {},
  onSave,
  isLoading = false,
}) => {
  const navigate = useNavigate();
  // ---------------------------------------------------------
  // 2️⃣ Create Survey Creator
  // ---------------------------------------------------------
  const [creator] = useState(() => {
    // =====================================================
    // 1️⃣ REGISTER CUSTOM THEME
    // =====================================================
    const customTheme = {
      themeName: "customTheme",
      cssVariables: { ...customThemeVariables },
      isPredefined: true,
    };

    const enLocale = getLocaleStrings("en");
    enLocale.creatortheme.names[customTheme.themeName] = "Custom Theme";
    registerCreatorTheme(customTheme);

    // =====================================================
    // 2️⃣ CREATE CREATOR
    // =====================================================
    const c = new SurveyCreator({
      showTranslationTab: true,
      showThemeTab: true,
      isAutoSave: false,
      showPreviewTab: true,
      showLogicTab: false,
      themeForPreview: "customTheme",
      allowModifyPages: false,
      showPagesToolbox: false,
      showCreatorThemeSettings: false,
      showJSONEditorTab: true,
    });

    c.survey.widthMode = "responsive";

    c.survey.supportedLocales = ["en", "hi", "gu"];
    c.survey.locale = "en";

    /* ADD THIS */
    c.translationLocalesOrder = ["hi", "gu"];
    // console.log(Serializer.findProperty("survey", "widthMode"));
    // =====================================================
    // 3️⃣ APPLY CUSTOM VISIBILITY RULES  ✅ NOW SAFE
    // =====================================================
    applySurveyPropertyVisibility(c);

    c.onCanShowProperty.add((_, options) => {
      console.log(
        "Property:",
        options.property?.name,
        "| Category:",
        options.property?.category,
      );
    });

    // =====================================================
    // 3️⃣ APPLY THEME
    // =====================================================
    c.applyCreatorTheme(customTheme);
    if (c.survey) {
      c.survey.applyTheme(customTheme);
    }

    try {
      c.toolbox.categories.forEach((cat) => (cat.collapsed = true));
    } catch { }

    // =====================================================
    // 4️⃣ RESET VALIDATION ON NEW QUESTION ADDED
    // =====================================================
    if (c.survey) {
      c.survey.onQuestionAdded.add((survey, options) => {
        const q = options.question;
        if (!q) return;

        // ---- FORCE RESET ----
        q.validationType = "";
        q.compareTo = undefined;
        q.compareValue = undefined;
        q.compareOperator = undefined;
        q.compareError = undefined;

        q.windowType = undefined;
        q.windowBaseQuestion = undefined;
        q.windowAmount = undefined;
        q.windowError = undefined;

        q.AcceptedInputs = undefined;
        q.valueCustomError = undefined;

        q.dependsOn = undefined;
        q.dependsValue = undefined;
        q.dependsError = undefined;
        q.dependencyAction = undefined;
        q.dependsSetValue = undefined;

        q.conditionalRangeShow = false;
        q.conditionalRangeRules = undefined;
        q.conditionalRangeBaseQuestion = undefined;

        q.rowRangeShow = false;
        q.rowRangeRules = undefined;
      });
    }

    // =====================================================
    // 5️⃣ SAFETY FOR dependencyAction PROPERTY
    // =====================================================
    c.onPropertyValueChanging.add((_, options: any) => {
      // ---- dependencyAction ----
      if (
        options.propertyName === "dependencyAction" &&
        options.newValue !== "setValue"
      ) {
        options.obj.dependsSetValue = undefined;
      }

      // ---- CLEAR FIELDS WHEN validationType IS CLEARED ----
      if (options.propertyName === "validationType" && !options.newValue) {
        const q = options.obj;
        q.compareTo = undefined;
        q.compareValue = undefined;
        q.compareOperator = undefined;
        q.compareError = undefined;

        q.windowType = undefined;
        q.windowBaseQuestion = undefined;
        q.windowAmount = undefined;
        q.windowError = undefined;

        q.AcceptedInputs = undefined;
        q.valueCustomError = undefined;

        q.dependsOn = undefined;
        q.dependsValue = undefined;
        q.dependsError = undefined;
        q.dependencyAction = undefined;
        q.dependsSetValue = undefined;

        q.conditionalRangeShow = false;
        q.conditionalRangeRules = undefined;
        q.conditionalRangeBaseQuestion = undefined;

        q.rowRangeShow = false;
        q.rowRangeRules = undefined;
      }
    });

    return c;
  });

  const toolbar = (creator as any).toolbarValue;

  const initializedRef = React.useRef<string | null>(null);

  useEffect(() => {
    // If creator exists and we have actual JSON data
    if (creator && initialJSON?.json) {
      // Use stringified JSON to track if data has actually changed
      const jsonStr = JSON.stringify(initialJSON.json);
      if (initializedRef.current !== jsonStr) {
        creator.JSON = initialJSON.json;
        initializedRef.current = jsonStr;
      }
    }
  }, [creator, initialJSON]);

  creator.onPropertyEditorCreated.add((creator: any, options: any) => {
    const propName = options.property?.name;

    if (
      propName !== "compareTo" &&
      propName !== "windowBaseQuestion" &&
      propName !== "dependsOn" &&
      propName !== "conditionalRangeBaseQuestion"
    ) {
      return;
    }

    const editor = options.editor;
    const survey = creator.survey;
    const currentQuestion = options.obj;

    if (!editor || !survey || !currentQuestion) return;

    const currentType = currentQuestion.inputType || currentQuestion.getType();

    // 2. NEW LOGIC: Match dependsSetValue input type to the current question
    if (propName === "dependsSetValue") {
      const supportedInputTypes = [
        "date",
        "time",
        "number",
        "datetime-local",
        "datetime",
      ];
      editor.inputType = supportedInputTypes.includes(currentType)
        ? currentType
        : "text";
      return; // We don't need choices for this, so we exit early
    }

    // ✅ Build choices manually
    editor.choices = survey
      .getAllQuestions()
      .filter((q: any) => {
        // ❌ exclude current question
        if (q.name === currentQuestion.name) return false;

        const targetType = q.getType();
        const targetInputType = q.inputType;

        // dependsOn → exclude matrix
        if (propName === "dependsOn") {
          return !targetType.startsWith("matrix");
        }

        // windowBaseQuestion → date only
        if (propName === "windowBaseQuestion") {
          return targetInputType === "date";
        }

        // conditionalRangeBaseQuestion → dropdown only
        if (propName === "conditionalRangeBaseQuestion") {
          return targetType === "dropdown" || targetType === "radiogroup";
        }

        // compareTo → same type
        if (propName === "compareTo") {
          if (currentType === "datetime" || currentType === "datetime-local") {
            return (
              targetInputType === "datetime" ||
              targetInputType === "datetime-local"
            );
          }
          return targetInputType === currentType;
        }

        return true;
      })
      .map((q: any) => ({
        value: q.name, // 🔐 stored value
        text: q.title || q.name, // 👀 displayed text
      }));
  });

  // ---------------------------------------------------------
  // 3️⃣ Define Toolbox Categories
  // ---------------------------------------------------------
  creator.toolbox.defineCategories(
    [
      {
        category: "Text Input Questions",
        items: [
          "text",
          { name: "comment", title: "Multi-Line Input" },
          "multipletext",
        ],
      },
      {
        category: "Choice-Based Questions",
        items: ["dropdown", "checkbox", "radiogroup", "tagbox"],
      },
      {
        category: "Matrices",
        items: ["matrix", "matrixdropdown"],
      },
      {
        category: "Panels",
        items: ["panel", "paneldynamic"],
      },
    ],
    false, //False to hide extra property
  );

  creator.toolbox.allowExpandMultipleCategories = true;
  creator.toolbox.showCategoryTitles = true;

  // creator.onCreatorLoaded.add(() => {
  //   creator.propertyGridModel.removeCategory("general");
  //   creator.propertyGridModel.removeCategory("data");
  // });
  const hiddenCategories = ["general"];

  creator.onCanShowProperty.add((sender, options) => {
    // options.property may be the property object, and it may have .category or .categoryName
    const cat =
      options.property &&
      (options.property.category ||
        options.property.categoryName ||
        options.property.category?.name);
    if (!cat) return;

    // category may be an object or a string - normalize to string
    const catName = typeof cat === "string" ? cat : cat.name || cat.title;

    if (hiddenCategories.includes(catName)) {
      options.show = false;
      options.property.visibleIf = false;
    }
  });

  // after you create `creator` instance
  creator.onPropertyEditorCreated.add((_: any, options: any) => {
    const prop = options.property;
    const editor = options.editor;

    if (!prop || !editor) return;

    // Allow clear only for specific types
    if (prop.type === "question" || prop.type === "dropdown") {
      editor.allowClear = true;
    }
  });

  //Conditioanl range template
  useEffect(() => {
    if (!creator) return;

    // Initialize the template generator logic
    const unregister = registerTemplateGenerators(creator);

    // Clean up on unmount
    return () => {
      unregister();
    };
  }, [creator]);

  // ---------------------------------------------------------
  // 4️⃣ Add Validation Logic (your full block unchanged)
  // ---------------------------------------------------------

  useEffect(() => {
    creator.onSurveyInstanceCreated.add((creatorInstance, options) => {
      const survey = options.survey;

      // -----------------------------------------------------
      // 🔥 APPLY DEPENDENCY SHOW / ENABLE
      // -----------------------------------------------------
      applyDependencyVisibility(survey);

      survey.onValueChanged.add((sender, change) => {
        handleDependencyValueChange(sender, change.name);
      });

      // -----------------------------------------------------
      // run for particular question
      // -----------------------------------------------------
      survey.onValidateQuestion.add((sender, options) => {
        const q = options.question;
        const v1 = options.value;

        if (!q) return;

        // -----------------------------------------------------
        // DEPENDENCY REQUIRE VALIDATION logic  <----- INSERT HERE
        // -----------------------------------------------------
        const depError = runDependencyRequireValidation(
          sender,
          options.question,
          options.value,
        );

        if (depError) {
          options.error = depError;
          return;
        }

        // -----------------------------------------------------
        //  Compare validation logic  <----- INSERT HERE
        // -----------------------------------------------------
        if (!runCompareValidation(sender, q, options)) return;

        // -----------------------------------------------------
        //  Window Period validation logic  <----- INSERT HERE
        // -----------------------------------------------------
        if (!runWindowValidation(sender, q, options)) return;

        // -----------------------------------------------------
        // CONDITIONAL RANGE + ROW RANGE VALIDATION
        // -----------------------------------------------------
        if (!runConditionalRangeValidation(sender, q, options)) return;

        //-----------------------------------
        // VALUE CHECK (Extra Rules Only)
        //-----------------------------------
        if (!runValueValidation(sender, q, options)) return;
        if (!runMinMaxValidation(sender, q, options)) return;
      });
    });
  }, [creator]);

  // ---------------------------------------------------------
  // 5️⃣ Remove Theme/JSON Tabs
  // ---------------------------------------------------------
  // useEffect(() => {
  //   creator.tabs = creator.tabs.filter((tab) => !["json"].includes(tab.id));
  // }, [creator]);

  // ---------------------------------------------------------
  // 6️⃣ Save CRF Useeffect
  // ---------------------------------------------------------
  useEffect(() => {
    if (!creator) return;

    toolbar.actions = toolbar.actions.filter(
      (action: any) =>
        !["action-undo", "action-redo", "svd-settings"].includes(action.id),
    );

    const saveCrfExists = toolbar.actions.some(
      (action: any) => action.id === "save-crf",
    );
    const backExists = toolbar.actions.some(
      (action: any) => action.id === "close",
    );

    if (!saveCrfExists) {
      toolbar.actions.push(
        new Action({
          id: "save-crf",
          title: "Save CRF",
          tooltip: "Save CRF",
          component: "sv-action-bar-item",
          visible: true,
          action: () => {
            const json = JSON.parse(creator.text);

            onSave({
              ...initialJSON,
              json,
            });
          },
        }),
      );
    }

    if (!backExists) {
      toolbar.actions.push(
        new Action({
          id: "close",
          title: "Back",
          tooltip: "Visit",
          component: "sv-action-bar-item",
          visible: true,
          action: () => {
            const hasData =
              initialJSON &&
              typeof initialJSON === "object" &&
              Object.keys(initialJSON).length > 0;
            navigate(
              hasData && !JSON.parse(sessionStorage.getItem("crfForEdit") || "[]")
                ? VISIT_PATHS.DETAILS
                : LIBRARY_PATHS.CRF_LIST,
            );
          },
        }),
      );
    }
  }, [creator]);

  // ---------------------------------------------------------
  // 7️⃣ UI
  // ---------------------------------------------------------
  return (
    <div style={{ height: "100vh" }}>
      {isLoading ? (
       <FullPageLoader/>
      ) : (
        <SurveyCreatorComponent creator={creator} />
      )}
    </div>
  );
};

export default SurveyBuilder;
